import React, { useState, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Button, Spinner } from 'react-bootstrap';

const LOGO_ASPECT = 345 / 249;
const SNAP_THRESHOLD = 10;
const CROP_WIDTH = 320;
const CROP_HEIGHT = 220;
const FALLBACK_MIN_ZOOM = 0.3;

const loadImage = imageSrc =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = imageSrc;
    });

const getCroppedImage = (imageSrc, cropPixels) =>
    new Promise(async (resolve, reject) => {
        try {
            const image = await loadImage(imageSrc);
            const canvas = document.createElement('canvas');
            canvas.width = cropPixels.width;
            canvas.height = cropPixels.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                cropPixels.x,
                cropPixels.y,
                cropPixels.width,
                cropPixels.height,
                0,
                0,
                cropPixels.width,
                cropPixels.height,
            );
            canvas.toBlob(resolve, 'image/png');
        } catch (error) {
            reject(error);
        }
    });

const LogoCropDialog = ({ image, onCancel, onConfirm }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [minZoom, setMinZoom] = useState(FALLBACK_MIN_ZOOM);
    const [zoom, setZoom] = useState(FALLBACK_MIN_ZOOM);
    const [pixels, setPixels] = useState(null);
    const [busy, setBusy] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    // Permite zoom-out para ver la imagen completa manteniendo la proporción
    // base (LOGO_ASPECT): el zoom inicial es el mínimo que contiene la imagen
    // dentro del recuadro de recorte.
    useEffect(() => {
        setCrop({ x: 0, y: 0 });
        setPixels(null);
        let cancelled = false;
        loadImage(image).then(img => {
            if (cancelled || !mountedRef.current) return;
            const cropHeight = Math.min(CROP_HEIGHT, CROP_WIDTH / LOGO_ASPECT);
            const cropWidth = cropHeight * LOGO_ASPECT;
            const cover = Math.max(cropWidth / img.naturalWidth, cropHeight / img.naturalHeight);
            const contain = Math.min(cropWidth / img.naturalWidth, cropHeight / img.naturalHeight);
            const fitZoom = Math.min(1, Math.max(0.05, contain / cover));
            setMinZoom(fitZoom);
            setZoom(fitZoom);
        }).catch(() => {
            if (cancelled || !mountedRef.current) return;
            setMinZoom(FALLBACK_MIN_ZOOM);
            setZoom(FALLBACK_MIN_ZOOM);
        });
        return () => { cancelled = true; };
    }, [image]);

    // Magnetic snap to center: {0,0} is the center in react-easy-crop.
    const onCropChange = value => {
        if (Math.abs(value.x) <= SNAP_THRESHOLD && Math.abs(value.y) <= SNAP_THRESHOLD) {
            setCrop({ x: 0, y: 0 });
        } else {
            setCrop(value);
        }
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => setPixels(croppedAreaPixels);

    const doConfirm = async () => {
        if (!pixels) return;
        setBusy(true);
        try {
            const blob = await getCroppedImage(image, pixels);
            if (mountedRef.current) {
                onConfirm(blob);
            }
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    return (
        <Modal show onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>Ajustar logo</Modal.Title>
            </Modal.Header>
            <Modal.Body className='d-flex flex-column align-items-center'>
                <div style={{ position: 'relative', width: 320, height: 220, background: '#fff' }}>
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        minZoom={minZoom}
                        maxZoom={3}
                        aspect={LOGO_ASPECT}
                        restrictPosition={false}
                        onCropChange={onCropChange}
                        onZoomChange={zoom => setZoom(zoom)}
                        onCropComplete={onCropComplete}
                    />
                    <div
                        style={{
                            position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1,
                            background: 'rgba(0,150,255,0.7)', pointerEvents: 'none', transform: 'translateX(-50%)',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute', top: '50%', left: 0, right: 0, height: 1,
                            background: 'rgba(0,150,255,0.7)', pointerEvents: 'none', transform: 'translateY(-50%)',
                        }}
                    />
                </div>
                <div className='d-flex align-items-center justify-content-center w-100 mt-3'>
                    <span className='mr-2 text-muted'>Zoom</span>
                    <input
                        type='range'
                        min={minZoom}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={e => setZoom(Number(e.target.value))}
                        className='mr-2'
                        style={{ maxWidth: 220, width: '100%' }}
                    />
                </div>
                <small className='text-muted mt-2'>Acercá el logo al centro; se ajustará solo.</small>
            </Modal.Body>
            <Modal.Footer className='justify-content-center'>
                <Button variant='success' onClick={doConfirm} disabled={busy || !pixels}>
                    {busy ? <Spinner animation='border' size='sm' /> : 'Usar este recorte'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LogoCropDialog;
